import React from 'react';
import { Users, Crown, AlertCircle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import TeamSwitcher from './TeamSwitcher';
import { Button } from './ui/button';

interface HeaderProps {
  teamName?: string;
  teamId?: string;
  onLogout?: () => void;
  onTeamClick?: () => void;
  showTeamSwitcher?: boolean;
  onUpgrade?: () => void;
  showUpgrade?: boolean;
  upgradeUrgent?: boolean;
}

const Header = ({ 
  teamName, 
  teamId, 
  onLogout, 
  onTeamClick,
  showTeamSwitcher = true,
  onUpgrade,
  showUpgrade = false,
  upgradeUrgent = false
}: HeaderProps = {}) => {
  const navigate = useNavigate();
  
  return (
    <header className="sticky top-0 z-50 border-b border-border bg-background shadow-sm">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-20">
          <div className="flex items-center">
            <img 
              src="https://cfcpcmswpbygvdbyrivt.supabase.co/storage/v1/object/sign/images/syncsimplelogo-color.png?token=eyJraWQiOiJzdG9yYWdlLXVybC1zaWduaW5nLWtleV9hMThjNGE0NC1hYzkzLTQwYTYtYTk5Yy0wY2NlYTdiZDZmM2UiLCJhbGciOiJIUzI1NiJ9.eyJ1cmwiOiJpbWFnZXMvc3luY3NpbXBsZWxvZ28tY29sb3IucG5nIiwiaWF0IjoxNzUyMzU0ODIxLCJleHAiOjE5MTAwMzQ4MjF9.geBqZOj9PK5jz1kE7zDLnYvzNqERaC7pVl9btt28T_I" 
              alt="SyncSimple Logo" 
              className="h-8 w-auto object-contain cursor-pointer hover:opacity-80 transition-opacity"
              onClick={() => navigate('/dashboard')}
            />
          </div>
          
          <nav className="flex items-center space-x-4">
            {/* Team Switcher - only show if we have team info */}
            {showTeamSwitcher && (teamId || teamName) && (
              <TeamSwitcher 
                currentTeamId={teamId}
                currentTeamName={teamName}
              />
            )}
            
            {/* Upgrade Button - show if trial is active or expired */}
            {showUpgrade && onUpgrade && (
              <Button
                type="button"
                variant={upgradeUrgent ? 'destructive' : 'default'}
                onClick={onUpgrade}
                className={`h-auto flex items-center space-x-2 rounded-md px-4 py-2 text-sm font-medium transition-colors ${
                  upgradeUrgent ? 'animate-pulse' : ''
                }`}
              >
                {upgradeUrgent ? (
                  <>
                    <AlertCircle className="h-4 w-4" />
                    <span className="font-bold">Trial Expired - Upgrade Now</span>
                  </>
                ) : (
                  <>
                    <Crown className="h-4 w-4" />
                    <span>Upgrade Plan</span>
                  </>
                )}
              </Button>
            )}

            {/* Team Settings Button - only show if onTeamClick is provided */}
            {onTeamClick && (
              <Button
                type="button"
                variant="ghost"
                onClick={onTeamClick}
                className="h-auto flex items-center space-x-2 rounded-md border border-border bg-background px-4 py-2 text-sm text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
              >
                <Users className="h-4 w-4" />
                <span className="font-medium text-sm">Settings</span>
              </Button>
            )}

            {onLogout && (
              <Button
                type="button"
                variant="outline"
                onClick={onLogout}
                className="h-auto flex items-center space-x-2 rounded-md border-destructive/20 bg-destructive/10 px-4 py-2 text-sm text-destructive transition-colors hover:bg-destructive/20 hover:text-destructive"
              >
                <span className="font-medium text-sm">Logout</span>
              </Button>
            )}
          </nav>
        </div>
      </div>
    </header>
  );
};

export default Header;
